import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { XMarkIcon, KeyIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import apiClient from '../../../api/axios';
import { ReminderProfileEditor } from './ReminderProfileEditor';
import type { TaskReminderProfile } from '../../../types/taskReminders';
import type { SignatureUserProfile } from '../types/digitalSignatureOnboarding';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isAttorney: boolean;
  isActive: boolean;
  roles: string[];
  createdAt: string;
  updatedAt: string;
  smsNumber?: string | null;
  reminderProfile?: TaskReminderProfile | null;
  signatureProfile?: SignatureUserProfile | null;
}

interface EditUserModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Simple Tooltip Component
interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const Tooltip: React.FC<TooltipProps> = ({ content, children, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-3',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-3',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-3',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-3'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900',
    bottom: 'bottom-full left-1/2 transform -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900',
    left: 'left-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-900',
    right: 'right-full top-1/2 transform -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-900'
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible && (
        <div className={`absolute z-[9999] ${positionClasses[position]}`}>
          <div className="bg-gray-900 text-white text-sm rounded-lg py-3 px-4 min-w-[280px] max-w-[320px] shadow-xl border border-gray-700 leading-relaxed">
            {content}
          </div>
          <div className={`absolute w-0 h-0 border-[6px] ${arrowClasses[position]}`} />
        </div>
      )}
    </div>
  );
};

const defaultSignatureProfile = (user: User): SignatureUserProfile => ({
  signer_display_name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
  can_sign_documents: false,
  mfa_confirmed_for_signing: false,
  esign_consent_accepted_at: null,
  esign_consent_version: null,
  allow_typed_signature: true,
  allow_drawn_signature: true,
  allow_uploaded_signature_image: false,
  signer_title: null,
  bar_number: null,
  signature_style_preference: null,
});

const getSignatureReadinessMissingItems = (profile: SignatureUserProfile | null): string[] => {
  if (!profile) {
    return [
      'Signer display name is required.',
      'Enable document signing permission.',
      'Confirm MFA requirement for signing.',
      'Record e-sign legal consent.',
    ];
  }

  const missing: string[] = [];
  if (!profile.signer_display_name || profile.signer_display_name.trim().length === 0) {
    missing.push('Signer display name is required.');
  }
  if (!profile.can_sign_documents) {
    missing.push('Enable document signing permission.');
  }
  if (!profile.mfa_confirmed_for_signing) {
    missing.push('Confirm MFA requirement for signing.');
  }
  if (!profile.esign_consent_accepted_at || profile.esign_consent_accepted_at.trim().length === 0) {
    missing.push('Record e-sign legal consent.');
  }

  return missing;
};

export const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    isAttorney: user.isAttorney,
    isActive: user.isActive,
    smsNumber: user.smsNumber || ''
  });
  const [reminderProfile, setReminderProfile] = useState<TaskReminderProfile | null>(user.reminderProfile || null);
  const [signatureProfile, setSignatureProfile] = useState<SignatureUserProfile>(user.signatureProfile || defaultSignatureProfile(user));

  const [selectedRoles, setSelectedRoles] = useState<string[]>(user.roles);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
    temporary: false
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [reminderProfileErrors, setReminderProfileErrors] = useState<string[]>([]);
  const queryClient = useQueryClient();

  // Update form data when user changes
  useEffect(() => {
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      isAttorney: user.isAttorney,
      isActive: user.isActive,
      smsNumber: user.smsNumber || ''
    });
    setSelectedRoles(user.roles);
    setReminderProfile(user.reminderProfile || null);
    setSignatureProfile(user.signatureProfile || defaultSignatureProfile(user));
    setPasswordData({ newPassword: '', confirmPassword: '', temporary: false });
    setActiveTab('profile');
  }, [user]);

  const updateUserMutation = useMutation({
    mutationFn: async (updateData: any) => {
      const sanitizedSms = typeof updateData.smsNumber === 'string' && updateData.smsNumber.trim().length === 0
        ? null
        : updateData.smsNumber;
      const response = await apiClient.patch(`/admin/users/${user.id}`, {
        ...updateData,
        roles: selectedRoles,
        smsNumber: sanitizedSms,
        reminderProfile: reminderProfile,
        signatureProfile: signatureProfile
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onSuccess();
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`/admin/users/${user.id}/reset-password`);
      return response.data;
    },
    onSuccess: () => {
      setShowSuccessModal(true);
    }
  });

  const setPasswordMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`/admin/users/${user.id}/set-password`, {
        password: passwordData.newPassword,
        temporary: passwordData.temporary
      });
      return response.data;
    },
    onSuccess: () => {
      setPasswordData({ newPassword: '', confirmPassword: '', temporary: false });
      setShowSuccessModal(true);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reminderProfileErrors.length > 0) {
      alert('Reminder profile has validation errors. Please fix them before saving.');
      return;
    }
    updateUserMutation.mutate(formData);
  };

  const handleRoleToggle = (role: string) => {
    setSelectedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const updateSignatureProfile = (patch: Partial<SignatureUserProfile>) => {
    setSignatureProfile((current) => ({
      ...current,
      ...patch,
    }));
  };

  const handleSetPassword = () => {
    if (passwordData.newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    setPasswordMutation.mutate();
  };

  // Role tooltips
  const getRoleTooltip = (role: string): string => {
    const tooltips = {
      admin: 'System Administration: Full access to user management, firm settings, and system configuration. Can manage all users and system-wide settings.',
      attorney: 'Attorney System Access: Can access attorney-specific features like matter management, legal templates, and attorney reports. This is about system permissions, not legal qualification.',
      billing: 'Billing Management: Access to billing features, invoice creation, payment tracking, and financial reports. Can manage client billing and payment processing.',
      assistant: 'Assistant Role: Basic access with support functions. Can assist with tasks but has limited administrative capabilities.',
      user: 'Standard User: Basic system access with standard user permissions. Default role for most team members.'
    };
    return tooltips[role as keyof typeof tooltips] || 'System role that determines what features this user can access.';
  };

  const missingSignatureReadiness = getSignatureReadinessMissingItems(signatureProfile);
  const signatureReady = missingSignatureReadiness.length === 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        {/* Fixed Header */}
        <div className="p-6 pb-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit User: {user.firstName} {user.lastName}
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <XMarkIcon className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
            <span><strong>Email:</strong> {user.email}</span>
            <span><strong>Created:</strong> {new Date(user.createdAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Form wraps tabs + footer so Save can submit */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
            <div className="px-6 pt-4 shrink-0">
              <TabsList className="w-full grid grid-cols-5 gap-1">
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="roles">Roles</TabsTrigger>
                <TabsTrigger value="reminders">Reminders</TabsTrigger>
                <TabsTrigger value="signature">Signature</TabsTrigger>
                <TabsTrigger value="password">Password</TabsTrigger>
              </TabsList>
            </div>

            {/* Scrollable Tab Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-[350px]">
              {/* Profile Tab */}
              <TabsContent value="profile">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        First Name *
                      </label>
                      <Input
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Name *
                      </label>
                      <Input
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SMS Number
                    </label>
                    <Input
                      value={formData.smsNumber}
                      onChange={(e) => setFormData(prev => ({ ...prev, smsNumber: e.target.value }))}
                      placeholder="+1 555-555-1212"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Used for SMS reminders when enabled in the reminder profile.
                    </p>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isAttorney}
                        onChange={(e) => setFormData(prev => ({ ...prev, isAttorney: e.target.checked }))}
                        className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                      />
                      <div className="flex items-center gap-2 ml-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Is Attorney
                        </span>
                        <Tooltip content="Professional legal qualification. Check this ONLY if this person is a licensed attorney qualified to practice law. This determines if they appear in 'Responsible Attorney' dropdowns for matters, attorney billing rates, and bar compliance reporting. This is separate from the 'Attorney' system role." position="right">
                          <QuestionMarkCircleIcon className="h-4 w-4 text-lex-accent hover:text-lex-accent-hover cursor-help" />
                        </Tooltip>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                      />
                      <div className="flex items-center gap-2 ml-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Account Active
                        </span>
                        <Tooltip content="Enable/disable user access. Inactive users cannot log in but their data is retained. Use this instead of deleting users when they leave the firm.">
                          <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Roles Tab */}
              <TabsContent value="roles">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      System Roles
                    </label>
                    <Tooltip content="System access permissions. These control what features and pages the user can access in EdgeLex. A user can have multiple roles (e.g., Admin + Attorney + Billing).">
                      <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                    </Tooltip>
                  </div>
                  <div className="space-y-3">
                    {['admin', 'attorney', 'billing', 'assistant', 'user'].map(role => (
                      <div key={role} className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <input
                          type="checkbox"
                          checked={selectedRoles.includes(role)}
                          onChange={() => handleRoleToggle(role)}
                          className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                              {role}
                            </span>
                            <Tooltip content={getRoleTooltip(role)} position="right">
                              <QuestionMarkCircleIcon className="h-3 w-3 text-gray-400 hover:text-gray-600 cursor-help" />
                            </Tooltip>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {role === 'admin' && 'Full system administration access'}
                            {role === 'attorney' && 'Attorney-specific features and templates'}
                            {role === 'billing' && 'Billing, invoicing, and financial reports'}
                            {role === 'assistant' && 'Support functions with limited admin access'}
                            {role === 'user' && 'Standard user permissions'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Reminders Tab */}
              <TabsContent value="reminders">
                <div className="space-y-4">
                  <div className={`rounded-md border px-3 py-2 text-xs ${
                    reminderProfile
                      ? 'border-lex-accent bg-lex-accent-soft text-lex-accent dark:border-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent'
                      : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-300'
                  }`}>
                    {reminderProfile
                      ? 'Custom reminder profile override is active for this user.'
                      : 'This user is currently using firm default reminders.'}
                    {reminderProfile && (
                      <button
                        type="button"
                        onClick={() => setReminderProfile(null)}
                        className="ml-2 underline"
                      >
                        Clear override
                      </button>
                    )}
                  </div>
                  <ReminderProfileEditor
                    value={reminderProfile}
                    onChange={(nextProfile) => setReminderProfile(nextProfile)}
                    title="User Reminder Profile"
                    description="Overrides firm defaults when set. Clear to fall back to firm reminders."
                    allowClear={true}
                    showTimezone={true}
                    onValidationChange={(_isValid, errors) => setReminderProfileErrors(errors)}
                  />
                </div>
              </TabsContent>

              {/* Digital Signature Tab */}
              <TabsContent value="signature">
                <div className="space-y-4">
                  <div className={`rounded-md border px-3 py-2 text-xs ${
                    signatureReady
                      ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'
                      : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300'
                  }`}>
                    {signatureReady
                      ? 'Signer onboarding is complete for this user.'
                      : `Signer onboarding is incomplete. ${missingSignatureReadiness.length} required item(s) remain.`}
                  </div>

                  {!signatureReady && (
                    <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/10 p-3">
                      <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">Missing Required Items</div>
                      <ul className="space-y-1 text-xs text-amber-700 dark:text-amber-300 list-disc pl-4">
                        {missingSignatureReadiness.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Signer Display Name *
                      </label>
                      <Input
                        value={signatureProfile.signer_display_name || ''}
                        onChange={(e) => updateSignatureProfile({ signer_display_name: e.target.value })}
                        placeholder="Name shown in signature evidence"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Signer Title
                      </label>
                      <Input
                        value={signatureProfile.signer_title || ''}
                        onChange={(e) => updateSignatureProfile({ signer_title: e.target.value })}
                        placeholder="Attorney at Law"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bar Number
                      </label>
                      <Input
                        value={signatureProfile.bar_number || ''}
                        onChange={(e) => updateSignatureProfile({ bar_number: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Signature Style Preference
                      </label>
                      <Input
                        value={signatureProfile.signature_style_preference || ''}
                        onChange={(e) => updateSignatureProfile({ signature_style_preference: e.target.value })}
                        placeholder="typed / drawn / uploaded"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-1">
                    <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2 rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                        checked={Boolean(signatureProfile.can_sign_documents)}
                        onChange={(e) => updateSignatureProfile({ can_sign_documents: e.target.checked })}
                      />
                      Allow this user to sign documents
                    </label>
                    <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        className="mr-2 rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                        checked={Boolean(signatureProfile.mfa_confirmed_for_signing)}
                        onChange={(e) => updateSignatureProfile({ mfa_confirmed_for_signing: e.target.checked })}
                      />
                      MFA confirmed for signing actions
                    </label>
                  </div>

                  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">Allowed Signature Methods</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                      <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          className="mr-2 rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                          checked={signatureProfile.allow_typed_signature !== false}
                          onChange={(e) => updateSignatureProfile({ allow_typed_signature: e.target.checked })}
                        />
                        Typed
                      </label>
                      <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          className="mr-2 rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                          checked={signatureProfile.allow_drawn_signature !== false}
                          onChange={(e) => updateSignatureProfile({ allow_drawn_signature: e.target.checked })}
                        />
                        Drawn
                      </label>
                      <label className="inline-flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          className="mr-2 rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                          checked={Boolean(signatureProfile.allow_uploaded_signature_image)}
                          onChange={(e) => updateSignatureProfile({ allow_uploaded_signature_image: e.target.checked })}
                        />
                        Uploaded Image
                      </label>
                    </div>
                  </div>

                  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">E-sign Consent</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Required before this user is signer-ready.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateSignatureProfile({
                          esign_consent_accepted_at: new Date().toISOString(),
                          esign_consent_version: signatureProfile.esign_consent_version || 'v1'
                        })}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        Mark Consent Now
                      </button>
                    </div>
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      {signatureProfile.esign_consent_accepted_at
                        ? `Accepted at ${new Date(signatureProfile.esign_consent_accepted_at).toLocaleString()}`
                        : 'No consent timestamp recorded yet.'}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Password Tab */}
              <TabsContent value="password">
                <div className="space-y-6">
                  {/* Set Password Section */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Set New Password
                    </h3>
                    <div className="space-y-3">
                      <Input
                        type="password"
                        placeholder="New password (min. 8 characters)"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      />
                      <Input
                        type="password"
                        placeholder="Confirm new password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      />
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={passwordData.temporary}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, temporary: e.target.checked }))}
                          className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                        />
                        <div className="flex items-center gap-2 ml-2">
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Require password change on next login
                          </span>
                          <Tooltip content="If checked, the user will be forced to change this password when they log in. Use this for temporary passwords or when security requires a password update.">
                            <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                          </Tooltip>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={handleSetPassword}
                        disabled={setPasswordMutation.isPending || !passwordData.newPassword || !passwordData.confirmPassword}
                        className="w-full"
                      >
                        <KeyIcon className="h-4 w-4 mr-2" />
                        {setPasswordMutation.isPending ? 'Setting...' : 'Set Password'}
                      </Button>
                    </div>
                  </div>

                  {/* Reset Password via Email Section */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Or Reset Password via Email
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      Send password reset instructions to user's email address
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => resetPasswordMutation.mutate()}
                      disabled={resetPasswordMutation.isPending}
                      className="w-full"
                    >
                      <KeyIcon className="h-4 w-4 mr-2" />
                      {resetPasswordMutation.isPending ? 'Sending...' : 'Send Reset Email'}
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </div>

          {/* Fixed Footer — hidden on password tab (it has its own buttons) */}
          {activeTab !== 'password' && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
              {(updateUserMutation.isError || resetPasswordMutation.isError || setPasswordMutation.isError) && (
                <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
                  <p className="text-red-600 dark:text-red-400 text-sm">
                    {updateUserMutation.isError && 'Failed to update user. Please try again.'}
                    {resetPasswordMutation.isError && 'Failed to reset password. Please try again.'}
                    {setPasswordMutation.isError && 'Failed to set password. Please try again.'}
                  </p>
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateUserMutation.isPending}>
                  {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
          </Tabs>
        </form>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] animate-in fade-in-0 duration-200"
            onClick={() => setShowSuccessModal(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-w-md w-full overflow-hidden animate-in zoom-in-95 fade-in-0 duration-200"
              role="dialog"
              aria-modal="true"
              aria-labelledby="success-modal-title"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 id="success-modal-title" className="text-lg font-semibold text-green-800 dark:text-green-200">
                    Success!
                  </h2>
                </div>
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="flex-shrink-0 w-8 h-8 rounded-lg text-green-500 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200 flex items-center justify-center"
                  aria-label="Close dialog"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <p className="text-gray-700 dark:text-gray-300 text-center">
                  {setPasswordMutation.isSuccess && resetPasswordMutation.isPending === false && (
                    <>
                      Password has been set successfully for <strong>{user.firstName} {user.lastName}</strong>.
                      {passwordData.temporary && (
                        <span className="block mt-2 text-sm text-gray-600 dark:text-gray-400">
                          The user will be required to change this password on their next login.
                        </span>
                      )}
                    </>
                  )}
                  {resetPasswordMutation.isSuccess && (
                    <>
                      Password reset email has been sent to <strong>{user.email}</strong>.
                      <span className="block mt-2 text-sm text-gray-600 dark:text-gray-400">
                        The user will receive instructions to reset their password.
                      </span>
                    </>
                  )}
                </p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-700/30">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
